import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import type { InverterPowerRow } from '@/api/powerplantDetail';
import { fetchInverterLineDrill } from '@/api/powerplantDetail';
import { useI18n } from '@/i18n';
import { localizeChartLabel } from '@/lib/chartLabel';

const TABS = [
  { key: 'hourly',  label: 'Saatlik', unit: 'kWh' },
  { key: 'daily',   label: 'Günlük',  unit: 'MWh' },
  { key: 'monthly', label: 'Aylık',   unit: 'MWh' },
  { key: 'yearly',  label: 'Yıllık',  unit: 'MWh' },
] as const;

type TabKey = typeof TABS[number]['key'];

const PALETTE = [
  '#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6',
  '#06b6d4','#f97316','#84cc16','#ec4899','#6366f1',
  '#14b8a6','#a855f7','#eab308','#22c55e','#0ea5e9',
];

interface Props {
  plantUuid: string;
  hourly:   InverterPowerRow[];
  daily:    InverterPowerRow[];
  monthly:  InverterPowerRow[];
  yearly:   InverterPowerRow[];
}

function pivot(rows: InverterPowerRow[]): {
  dates: string[];
  inverters: string[];
  seriesData: Record<string, number[]>;
  dateItems: InverterPowerRow[];
} {
  const dateMap = new Map<string, InverterPowerRow>();
  const readings = new Map<string, Map<string, number>>();
  for (const row of rows) {
    if (!dateMap.has(row.date_name)) dateMap.set(row.date_name, row);
    let values = readings.get(row.inverter_name);
    if (!values) { values = new Map(); readings.set(row.inverter_name, values); }
    // Match the previous first-reading behavior when duplicate rows exist.
    if (!values.has(row.date_name)) values.set(row.date_name, Number(row.power_total) || 0);
  }
  const dates = Array.from(dateMap.keys());
  const dateItems = Array.from(dateMap.values());
  const inverters = Array.from(readings.keys());
  const seriesData: Record<string, number[]> = {};
  for (const [inverter, values] of readings) {
    seriesData[inverter] = dates.map(date => values.get(date) ?? 0);
  }
  return { dates, inverters, seriesData, dateItems };
}

function HighchartsMultiLineChart({ options, height }: { options: Highcharts.Options; height: number }) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const chart = Highcharts.chart(containerRef.current, options);
    return () => chart.destroy();
  }, [options]);

  return <div ref={containerRef} style={{ height: `${height}px` }} className="w-full" />;
}

function InverterLineChart({ plantUuid, hourly, daily, monthly, yearly }: Props) {
  const { t } = useI18n();
  const [active, setActive] = useState<TabKey>('monthly');
  const [drillYear,  setDrillYear]  = useState<number | null>(null);
  const [drillMonth, setDrillMonth] = useState<number | null>(null);
  const [drillDay,   setDrillDay]   = useState<number | null>(null);
  const [drillRows,  setDrillRows]  = useState<InverterPowerRow[] | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const activeRef = useRef(active);
  activeRef.current = active;

  const baseMap: Record<TabKey, InverterPowerRow[]> = { hourly, daily, monthly, yearly };

  const rawRows = drillRows ?? baseMap[active];
  const { dates, inverters, seriesData, dateItems } = useMemo(() => pivot(rawRows), [rawRows]);
  const localizedDates = useMemo(
    () => dates.map((label) => localizeChartLabel(label, t)),
    [dates, t],
  );
  const tab = TABS.find(t => t.key === active)!;

  const doDrill = useCallback(async (year: number, month: number | null, day: number | null) => {
    setDrillLoading(true);
    try {
      const rows = await fetchInverterLineDrill(plantUuid, year, month, day);
      setDrillRows(rows);
    } finally {
      setDrillLoading(false);
    }
  }, [plantUuid]);

  const doDrillRef = useRef(doDrill);
  doDrillRef.current = doDrill;

  function handleTabClick(key: TabKey) {
    setActive(key);
    setDrillYear(null); setDrillMonth(null); setDrillDay(null); setDrillRows(null);
  }

  function handleBack() {
    if (active === 'hourly') {
      setActive('daily'); setDrillDay(null); setDrillRows(null);
      if (drillYear !== null && drillMonth !== null) void doDrill(drillYear, drillMonth, null);
    } else if (active === 'daily') {
      setActive('monthly'); setDrillMonth(null); setDrillDay(null); setDrillRows(null);
      if (drillYear !== null) void doDrill(drillYear, null, null);
    } else if (active === 'monthly') {
      setActive('yearly'); setDrillYear(null); setDrillMonth(null); setDrillRows(null);
    }
  }

  const drillLabel = useMemo(() => {
    if (active === 'hourly' && drillYear !== null && drillMonth !== null && drillDay !== null) {
      const item = dateItems[0];
      return item?.date_name ? `${localizeChartLabel(item.date_name.slice(0, 6), t)} ${drillYear}` : `${drillDay}/${drillMonth}/${drillYear}`;
    }
    if (active === 'daily' && drillYear !== null && drillMonth !== null) {
      const item = dateItems.find(d => d.date_month === drillMonth);
      return `${localizeChartLabel(item?.date_name?.slice(0, 3) ?? String(drillMonth), t)} ${drillYear}`;
    }
    if (active === 'monthly' && drillYear !== null) return `${drillYear}`;
    return null;
  }, [active, drillYear, drillMonth, drillDay, dateItems, t]);

  const canDrillDown = active !== 'hourly';

  const highchartsOptions = useMemo<Highcharts.Options>(() => ({
    chart: {
      type: 'line',
      backgroundColor: 'transparent',
      spacingTop: 4,
      spacingLeft: 0,
      spacingRight: 0,
      spacingBottom: 4,
      style: { fontFamily: 'Inter, system-ui, sans-serif' },
    },
    title: { text: undefined },
    credits: { enabled: false },
    legend: {
      enabled: inverters.length <= 10,
      itemStyle: { fontSize: '10px', fontWeight: '500', color: '#64748b' },
      itemHoverStyle: { color: '#0f172a' },
    },
    xAxis: {
      categories: localizedDates,
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
      labels: {
        style: { color: '#64748b', fontSize: '10px', fontWeight: '600' },
        rotation: dates.length > 20 ? -45 : 0,
      },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: '#f8fafc',
      gridLineDashStyle: 'Dash',
      labels: { style: { color: '#94a3b8', fontSize: '10px' } },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      borderRadius: 12,
      borderColor: '#cbd5e1',
      backgroundColor: 'rgba(255,255,255,0.96)',
      shadow: false,
    },
    plotOptions: {
      line: {
        lineWidth: 1.5,
        marker: {
          enabled: dates.length <= 15,
          radius: 3,
          symbol: 'circle',
          lineWidth: 1.5,
          lineColor: '#ffffff',
        },
        cursor: canDrillDown ? 'pointer' : 'default',
        point: {
          events: {
            click: function () {
              const currentTab = activeRef.current;
              if (currentTab === 'hourly') return;
              const idx = this.index;
              const item = dateItems[idx];
              if (!item) return;

              if (currentTab === 'yearly' && item.date_year != null) {
                setDrillYear(item.date_year); setDrillMonth(null); setDrillDay(null); setDrillRows(null);
                setActive('monthly');
                void doDrillRef.current(item.date_year, null, null);
              } else if (currentTab === 'monthly' && item.date_year != null && item.date_month != null) {
                setDrillYear(item.date_year); setDrillMonth(item.date_month); setDrillDay(null); setDrillRows(null);
                setActive('daily');
                void doDrillRef.current(item.date_year, item.date_month, null);
              } else if (currentTab === 'daily' && item.date_year != null && item.date_month != null && item.date_day != null) {
                setDrillYear(item.date_year); setDrillMonth(item.date_month); setDrillDay(item.date_day); setDrillRows(null);
                setActive('hourly');
                void doDrillRef.current(item.date_year, item.date_month, item.date_day);
              }
            },
          },
        },
      },
      series: {
        animation: { duration: 350 },
        states: { inactive: { opacity: 0.4 } },
      },
    },
    series: inverters.map((inv, i) => ({
      type: 'line' as const,
      name: inv,
      color: PALETTE[i % PALETTE.length],
      data: seriesData[inv],
    })),
  }), [localizedDates, dates, inverters, seriesData, dateItems, canDrillDown]);

  return (
    <div className="rounded-[28px] border border-slate-100 bg-white p-6 shadow-[0_20px_60px_-32px_rgba(15,23,42,0.35)] transition-all flex flex-col">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 min-w-0">
          {drillLabel && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-[9px] font-bold text-slate-400 hover:text-slate-700 transition-colors shrink-0"
            >
              ←
            </button>
          )}
          <h2 className="text-[12px] font-black text-slate-900 tracking-tight leading-none truncate">
            İnverter Üretim Durumu
            {drillLabel && (
              <span className="ml-1.5 text-[10px] font-semibold text-slate-400">{drillLabel}</span>
            )}
          </h2>
        </div>
        <div className="flex overflow-hidden rounded-xl border border-slate-100 bg-slate-50/50 shadow-sm">
          {TABS.map(tabDef => (
            <button
              key={tabDef.key}
              onClick={() => handleTabClick(tabDef.key)}
              className={`px-3 py-1 text-[9px] font-bold transition-all ${
                active === tabDef.key ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-500 hover:bg-white hover:text-slate-900'
              }`}
            >
              {tabDef.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative">
        <HighchartsMultiLineChart options={highchartsOptions} height={300} />
        {drillLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl">
            <div className="h-5 w-5 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" />
          </div>
        )}
      </div>

      <p className="mt-4 text-sm text-slate-400">
        {active === 'hourly'  && `Son 24 saat (${tab.unit})`}
        {active === 'daily'   && `Son 30 gün (${tab.unit})`}
        {active === 'monthly' && `Son 12 ay (${tab.unit})`}
        {active === 'yearly'  && `Son 5 yıl (${tab.unit})`}
      </p>
    </div>
  );
}

export default memo(InverterLineChart);
